import { createRoot } from 'react-dom/client';
import { AnalysisReport } from '@/components/AnalysisReport';
import { generatePrintHTML } from './printUtils';

/**
 * Export analysis or comparison data to an accessible PDF using browser print.
 *
 * Uses a hidden same-origin <iframe> instead of window.open() so that Safari's
 * popup blocker is never triggered. iframe.contentWindow.print() opens the
 * native print dialog directly and is never subject to popup blocking.
 * The iframe is full-viewport so Recharts / ResizeObserver measure real
 * dimensions and render charts at the correct size before printing.
 */
export async function exportToAccessiblePDF(
  analysisData: any,
  options: {
    filename?: string;
    documentName?: string;
    title?: string;
    contentHTML?: string; // Pre-rendered HTML for comparisons
  } = {}
): Promise<void> {
  try {
    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const documentName = options.documentName
      ? sanitizeFilename(options.documentName)
      : 'Document';
    const filename = options.filename || `Analysis_${documentName}_${timestamp}.pdf`;

    let title: string;
    const isPreRendered = Boolean(options.contentHTML);

    // Create a hidden full-viewport iframe. Full-viewport (with opacity:0) lets
    // Recharts measure real dimensions. pointer-events:none keeps it non-interactive.
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:0;top:0;width:100%;height:100%;' +
      'opacity:0;pointer-events:none;border:0;z-index:-1;';
    document.body.appendChild(iframe);

    const iframeWin = iframe.contentWindow;
    const iframeDoc = iframe.contentDocument || iframeWin?.document;

    if (!iframeDoc || !iframeWin) {
      document.body.removeChild(iframe);
      throw new Error('Could not initialise print frame.');
    }

    const isSafari = (() => {
      const ua = iframeWin.navigator.userAgent;
      return /Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua);
    })();

    if (isPreRendered) {
      title = options.title || 'Comparison Report';
      const printHTML = generatePrintHTML(title, options.contentHTML!);
      iframeDoc.open();
      iframeDoc.write(printHTML);
      iframeDoc.close();
    } else {
      title = `Analysis Report - ${
        analysisData.filename || analysisData.original_filename || 'Document'
      }`;
      const shellHTML = generatePrintHTML(title, '<div id="react-root"></div>');
      iframeDoc.open();
      iframeDoc.write(shellHTML);
      iframeDoc.close();
      const rootEl = iframeDoc.getElementById('react-root');
      if (rootEl) {
        const root = createRoot(rootEl);
        root.render(<AnalysisReport analysisData={analysisData} />);
      }
    }

    await new Promise<void>((resolve) => {
      const cleanup = () => {
        try {
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
        } catch {}
        resolve();
      };

      // Use setTimeout instead of onload: Safari fires onload synchronously
      // inside document.close() for inline HTML (no external resources), so
      // setting onload after document.close() would miss the event entirely.
      setTimeout(() => {
        const checkChartsRendered = () => {
          const charts = iframeDoc.querySelectorAll('.recharts-wrapper');
          if (charts.length === 0) return true;
          const svgs = iframeDoc.querySelectorAll('.recharts-wrapper svg');
          console.log(`Found ${svgs.length} Recharts SVG elements`);
          return svgs.length >= charts.length;
        };

        const scaleHeatmapToSinglePage = () => {
          const wrappers = Array.from(
            iframeDoc.querySelectorAll('.heatmap-print-wrapper')
          ) as HTMLElement[];
          if (wrappers.length === 0) return;

          if (isSafari) {
            const safariStyle = iframeDoc.createElement('style');
            safariStyle.textContent = `
              @page { size: Letter landscape !important; margin: 0.5in !important; }
              @media print {
                html, body { width: auto !important; height: auto !important; }
                body { padding: 0 !important; }
                .heatmap-print-wrapper {
                  width: 100% !important; max-width: none !important; overflow: visible !important;
                }
                .heatmap-print-wrapper .overflow-x-auto {
                  overflow: visible !important; width: 100% !important;
                }

                /* Tighten the 2rem space-y-8 gaps between card sections
                   (title / grid / legend / summary) — vertical slack directly
                   shrinks the one-page scale factor */
                .plo-heatmap-print > * + *, .clo-heatmap-print > * + * { margin-top: 0.6rem !important; }

                /* PLO */
                .plo-heatmap-print { padding: 0.75rem !important; gap: 0.6rem !important; margin-bottom: 0 !important; }
                .plo-heatmap-header { margin-bottom: 0 !important; }
                .plo-heatmap-title { font-size: 14pt !important; line-height: 1.15 !important; margin-bottom: 0.1rem !important; }
                .plo-heatmap-meta { font-size: 8pt !important; line-height: 1.15 !important; margin: 0 !important; }
                .plo-heatmap-scroller { overflow: visible !important; }
                .plo-heatmap-grid { display: inline-grid !important; width: auto !important; min-width: max-content !important; }
                .plo-heatmap-header-cell { padding: 0.3rem 0.35rem !important; font-size: 6.5pt !important; line-height: 1.1 !important; min-width: 0 !important; max-width: 100% !important; }
                .plo-heatmap-col-header { overflow-wrap: anywhere !important; word-break: break-word !important; }
                /* margin/border/radius: undo the print stylesheet's .bg-white
                   CARD styling, which otherwise inflates every label cell into
                   a floating pill with a gray band under it */
                .plo-heatmap-label-cell { padding: 0.35rem 0.4rem !important; gap: 0.3rem !important; min-width: 0 !important; max-width: 100% !important; margin: 0 !important; border: none !important; border-radius: 0 !important; }
                .plo-heatmap-label-text { font-size: 7.5pt !important; line-height: 1.1 !important; overflow-wrap: anywhere !important; }
                .plo-heatmap-value-cell { padding: 0.2rem !important; min-height: 30px !important; min-width: 0 !important; }
                .plo-heatmap-value-text { font-size: 8pt !important; line-height: 1 !important; }
                .plo-heatmap-badge { width: 0.85rem !important; height: 0.85rem !important; font-size: 6px !important; top: 0.1rem !important; right: 0.1rem !important; }
                .plo-heatmap-legend { gap: 0.4rem !important; margin-top: 0 !important; }
                .plo-heatmap-legend-item { gap: 0.2rem !important; }
                .plo-heatmap-legend-swatch { width: 0.9rem !important; height: 0.9rem !important; }
                .plo-heatmap-legend span, .plo-heatmap-summary p, .plo-heatmap-summary h3 { font-size: 7pt !important; line-height: 1.15 !important; }
                .plo-heatmap-summary { padding: 0.5rem !important; }

                /* CLO */
                .clo-heatmap-print { padding: 0.75rem !important; gap: 0.6rem !important; margin-bottom: 0 !important; }
                .clo-heatmap-header { margin-bottom: 0 !important; }
                .clo-heatmap-title { font-size: 14pt !important; line-height: 1.15 !important; margin-bottom: 0.1rem !important; }
                .clo-heatmap-meta { font-size: 8pt !important; line-height: 1.15 !important; margin: 0 !important; }
                .clo-heatmap-scroller { overflow: visible !important; }
                .clo-heatmap-grid { display: inline-grid !important; width: auto !important; min-width: max-content !important; }
                .clo-heatmap-header-cell { padding: 0.3rem 0.35rem !important; font-size: 6.5pt !important; line-height: 1.1 !important; min-width: 0 !important; max-width: 100% !important; }
                .clo-heatmap-col-header { overflow-wrap: anywhere !important; word-break: break-word !important; }
                .clo-heatmap-label-cell { padding: 0.35rem 0.4rem !important; gap: 0.3rem !important; min-width: 0 !important; max-width: 100% !important; margin: 0 !important; border: none !important; border-radius: 0 !important; }
                .clo-heatmap-label-text { font-size: 7.5pt !important; line-height: 1.1 !important; overflow-wrap: anywhere !important; }
                .clo-heatmap-value-cell { padding: 0.2rem !important; min-height: 30px !important; min-width: 0 !important; }
                .clo-heatmap-value-text { font-size: 8pt !important; line-height: 1 !important; }
                .clo-heatmap-legend { gap: 0.4rem !important; margin-top: 0 !important; }
                .clo-heatmap-legend-item { gap: 0.2rem !important; }
                .clo-heatmap-legend-swatch { width: 0.9rem !important; height: 0.9rem !important; }
                .clo-heatmap-legend span, .clo-heatmap-summary p, .clo-heatmap-summary h3 { font-size: 7pt !important; line-height: 1.15 !important; }
                .clo-heatmap-summary { padding: 0.5rem !important; }
              }
            `;
            iframeDoc.head.appendChild(safariStyle);

            // Apply the @media print rules (incl. the shrink rules above) on
            // screen so the measurements below reflect the printed layout.
            flattenPrintStylesForSafari();

            // Conservative Letter-landscape printable box. Safari ignores @page
            // margins and substitutes its own, so we under-estimate the usable
            // area to guarantee the scaled heatmap lands on ONE page. WebKit
            // ignores break-inside: avoid, so anything spilling onto a second
            // page gets its grid rows sliced mid-text — fitting the whole
            // heatmap to a single page is the only reliable way to stop that.
            const printableW = 940;
            const printableH = 680;
            const pageRatio = printableW / printableH;

            for (const wrapper of wrappers) {
              wrapper.style.overflow = 'visible';

              const root =
                (wrapper.querySelector('.clo-heatmap-print, .plo-heatmap-print') as HTMLElement | null) ||
                wrapper;
              root.style.transform = 'none';
              root.style.zoom = '1';

              const grid = root.querySelector(
                '.clo-heatmap-grid, .plo-heatmap-grid'
              ) as HTMLElement | null;
              if (grid) {
                const headerSel = grid.classList.contains('clo-heatmap-grid')
                  ? '.clo-heatmap-col-header'
                  : '.plo-heatmap-col-header';
                const courseCount = grid.querySelectorAll(headerSel).length;
                // The card is shrunk to 0.75rem padding per side by the rules above.
                const cardPad = 24;

                // Give the grid an explicit width AND pin the card (the element
                // that draws the border) to grid width + padding. Safari re-lays
                // the document out at its own width when printing; with the card
                // left at width:auto it tracks that body width while the grid
                // stays at its pinned px width — so the grid pokes out of the
                // border. Fixed px on both keeps them glued together no matter
                // what width Safari prints at. setProperty(..., 'important') is
                // required to beat the injected `width: auto !important` and the
                // print stylesheet's `div { max-width: 100% !important }`.
                const applyWidth = (gridW: number) => {
                  grid.style.setProperty('width', `${gridW}px`, 'important');
                  grid.style.setProperty('min-width', '0', 'important');
                  grid.style.setProperty('max-width', 'none', 'important');
                  if (courseCount > 0) {
                    grid.style.setProperty(
                      'grid-template-columns',
                      `minmax(260px, 3fr) repeat(${courseCount}, minmax(56px, 1fr))`,
                      'important'
                    );
                  }
                  root.style.setProperty('width', `${gridW + cardPad}px`, 'important');
                  root.style.setProperty('max-width', 'none', 'important');
                };

                // Match the card's aspect ratio to the printable page so the
                // height-fitting scale below also fills the full page width.
                // Widening the grid shortens it too (labels wrap less), so a
                // fixed-point iteration on W = pageRatio * H(W) converges in a
                // few steps; the clamp guards degenerate content.
                let gridW = printableW - cardPad;
                applyWidth(gridW);
                for (let i = 0; i < 6; i++) {
                  const h = Math.max(root.scrollHeight, root.getBoundingClientRect().height);
                  const target = Math.min(
                    Math.max(Math.round(h * pageRatio) - cardPad, 500),
                    2600
                  );
                  if (Math.abs(target - gridW) <= 10) break;
                  gridW = target;
                  applyWidth(gridW);
                }
              }

              // Scale the whole heatmap (header + grid + legend + summary) to fit
              // one page in BOTH dimensions. transform: scale() is honored by
              // WebKit's print engine (zoom is not); collapsing the wrapper box
              // to the scaled size stops Safari adding a blank page.
              const naturalW = Math.max(root.scrollWidth, root.getBoundingClientRect().width);
              const naturalH = Math.max(root.scrollHeight, root.getBoundingClientRect().height);
              if (!naturalW || !naturalH) continue;
              const scale = Math.min(printableW / naturalW, printableH / naturalH, 1);
              if (scale < 1) {
                root.style.transformOrigin = 'top left';
                root.style.transform = `scale(${scale.toFixed(4)})`;
              }
              // Transform is visual-only; collapse the layout box to the scaled
              // footprint so the page reserves only the space actually used.
              // setProperty(..., 'important') because the injected
              // `.heatmap-print-wrapper { width: 100% !important }` rule beats
              // plain inline styles. Clip the wrapper too: the print engine
              // paginates by the UNSCALED layout box, so without clipping the
              // card still registers as taller than a page and gets split.
              wrapper.style.setProperty('width', `${Math.ceil(naturalW * scale)}px`, 'important');
              wrapper.style.setProperty('height', `${Math.ceil(naturalH * scale)}px`, 'important');
              wrapper.style.setProperty('overflow', 'hidden', 'important');
            }

            return;
          }

          // Non-Safari: scale each wrapper independently
          for (const wrapper of wrappers) {
            const naturalW = wrapper.scrollWidth;
            const naturalH = wrapper.scrollHeight;
            if (!naturalW || !naturalH) continue;
            const printableW = (11 - 1.0) * 96; // 960px
            const printableH = (8.5 - 1.0) * 96; // 720px
            const scale = Math.min(printableW / naturalW, printableH / naturalH, 1);
            if (scale >= 1) continue;
            wrapper.style.zoom = scale.toFixed(4);
            const pageWIn = ((naturalW * scale) / 96 + 1.0).toFixed(3);
            const pageHIn = ((naturalH * scale) / 96 + 1.0).toFixed(3);
            const style = iframeDoc.createElement('style');
            style.textContent = `@page { size: ${pageWIn}in ${pageHIn}in !important; margin: 0.5in !important; }`;
            iframeDoc.head.appendChild(style);
          }
        };

        const scaleProblemChartToFitPage = () => {
          const inner = iframeDoc.querySelector(
            '.print-chart-container > .space-y-3'
          ) as HTMLElement | null;
          if (!inner) return;
          // scrollWidth reflects layout size — transform doesn't affect it
          const naturalW = inner.scrollWidth;
          if (!naturalW) return;
          // Letter portrait, 0.5 in margins each side → 7.5 in printable = 720 px at 96 dpi
          const printableW = 7.5 * 96;
          if (naturalW <= printableW) return;
          const scale = printableW / naturalW;
          // zoom shrinks the layout box (unlike transform which only affects visuals)
          inner.style.transform = 'none';
          inner.style.zoom = String(scale);
        };

        // Safari: freeze Recharts charts as static clones BEFORE narrowing the
        // iframe. Live charts re-render at the new width via ResizeObserver and
        // get squished (fixed axis margins eat the narrow width). A static clone
        // keeps the wide rendering and scales down proportionally via the print
        // CSS (svg { max-width:100%; height:auto } + viewBox) — matching Chrome,
        // which never re-runs JS at print time.
        const freezeRechartsForSafari = () => {
          const containers = Array.from(
            iframeDoc.querySelectorAll('.recharts-responsive-container')
          ) as HTMLElement[];
          if (containers.length === 0) return;

          const overrideStyle = iframeDoc.createElement('style');
          overrideStyle.textContent = `
            .recharts-frozen { min-height: 0 !important; width: 100% !important; height: auto !important; }
            .recharts-frozen .recharts-wrapper { width: 100% !important; height: auto !important; }
            /* Scale only the main chart svg — small svgs (legend swatches, icons)
               must keep their intrinsic size */
            .recharts-frozen .recharts-wrapper > svg {
              max-width: 100% !important; width: 100% !important; height: auto !important;
            }
          `;
          iframeDoc.head.appendChild(overrideStyle);

          for (const live of containers) {
            const clone = live.cloneNode(true) as HTMLElement;
            clone.classList.add('recharts-frozen');
            live.parentElement?.replaceChild(clone, live);
            // The chart's host div often has a fixed pixel height — relax it so
            // the proportionally scaled SVG doesn't leave a gap or get clipped
            const host = clone.parentElement;
            if (host && host.style.height) host.style.height = 'auto';
          }
        };

        // Safari ignores zoom when printing, so fit the chart with transform:scale()
        // instead (reliably honored by WebKit's print engine) and shrink the
        // container's layout box by hand so nothing downstream overlaps or clips.
        const fitProblemChartForSafari = () => {
          const container = iframeDoc.querySelector('.print-chart-container') as HTMLElement | null;
          const inner = container?.querySelector(':scope > .space-y-3') as HTMLElement | null;
          if (!container || !inner) return;

          // Neutralize the print CSS zoom rules so they don't compound with the transform
          inner.style.zoom = '1';

          const printableW = 7.5 * 96; // 720px
          const naturalW = Math.max(inner.scrollWidth, inner.getBoundingClientRect().width);
          if (!naturalW || naturalW <= printableW) return;

          const scale = printableW / naturalW;
          const naturalH = inner.scrollHeight;
          inner.style.transformOrigin = 'top left';
          inner.style.transform = `scale(${scale.toFixed(4)})`;
          // Transform is visual-only; collapse the layout box to the visual size
          container.style.width = `${printableW}px`;
          container.style.height = `${Math.ceil(naturalH * scale)}px`;
          container.style.overflow = 'visible';
        };

        // Safari: copy every rule inside @media print into a plain <style> so the
        // print styles also apply on screen inside the hidden iframe. This makes
        // on-screen measurements match the printed layout exactly, which the
        // manual pagination below depends on.
        const flattenPrintStylesForSafari = () => {
          let css = '';
          for (const sheet of Array.from(iframeDoc.styleSheets)) {
            let rules: CSSRuleList;
            try {
              rules = sheet.cssRules;
            } catch {
              continue;
            }
            for (const rule of Array.from(rules)) {
              const mediaRule = rule as CSSMediaRule;
              if (mediaRule.media?.mediaText?.includes('print') && mediaRule.cssRules) {
                for (const inner of Array.from(mediaRule.cssRules)) {
                  css += inner.cssText + '\n';
                }
              }
            }
          }
          const style = iframeDoc.createElement('style');
          style.textContent = css;
          iframeDoc.head.appendChild(style);
        };

        // Safari: WebKit has never supported break-inside: avoid, so cards get
        // split mid-text across pages. Emulate Chrome's behavior in JS: walk the
        // same elements the print CSS marks as unbreakable and, when one would
        // straddle a page boundary, insert a spacer to push it to the next page.
        const paginateForSafariPrint = () => {
          // Conservative page capacity: Safari ignores @page margins and applies
          // its own, so the exact printable height is unknowable. Forced breaks
          // (which WebKit has always supported, unlike break-inside: avoid) reset
          // the page position exactly, so per-page error can never accumulate —
          // we only need pageH to be ≤ the real capacity.
          const pageH = 900;
          const bodyTop = iframeDoc.body.getBoundingClientRect().top;
          // Two granularities, mirroring what Chrome's print engine does natively:
          // - cards/charts the print CSS marks unbreakable (break-inside: avoid)
          // - individual text blocks (p, li, headings, rows) inside cards that are
          //   taller than a page — Chrome breaks BETWEEN lines, WebKit slices
          //   through them, so we move whole text blocks to the next page instead.
          const candidates = Array.from(
            iframeDoc.querySelectorAll(
              'div.pdf-page-break-avoid, div.bg-gray-50, .grid > div, div.rounded-lg.border, ' +
                '.recharts-frozen, .print-chart-container, ' +
                'p, li, blockquote, tr, h1, h2, h3, h4, h5, h6'
            )
          ) as HTMLElement[];

          // Greedy pagination: track where the current print page starts in flow
          // coordinates; force a break on the first element that would cross it.
          let pageStart = 0;
          for (const el of candidates) {
            const rect = el.getBoundingClientRect();
            // Skip empty elements and anything taller than a page (must split anyway)
            if (!rect.height || rect.height > pageH * 0.9) continue;
            const top = rect.top - bodyTop;
            const localTop = top - pageStart;
            if (localTop < 0) continue;
            const pageIdx = Math.floor(localTop / pageH);
            const offsetInPage = localTop - pageIdx * pageH;
            const crosses = offsetInPage + rect.height > pageH;
            // Keep headings with their content (Chrome's break-after: avoid):
            // a heading with <60px left below it would orphan from its content.
            const orphanedHeading =
              !crosses &&
              /^H[1-6]$/.test(el.tagName) &&
              pageH - (offsetInPage + rect.height) < 60;
            if (!crosses && !orphanedHeading) continue;

            const parent = el.parentElement;
            if (!parent) continue;
            if (iframeWin.getComputedStyle(parent).display.includes('grid')) {
              // Forced breaks don't work on grid items — pad via margin instead
              // (grid margins don't collapse, so the shift is exact).
              const push = (pageIdx + 1) * pageH - localTop;
              const currentMargin = parseFloat(iframeWin.getComputedStyle(el).marginTop) || 0;
              el.style.marginTop = `${currentMargin + push}px`;
              pageStart = top + push;
            } else {
              el.style.pageBreakBefore = 'always';
              el.style.breakBefore = 'page';
              pageStart = top;
            }
          }
        };

        const finishPrint = () => {
          // onafterprint fires when the print dialog is dismissed (print or cancel)
          iframeWin.onafterprint = cleanup;

          if (iframeWin.matchMedia) {
            const mql = iframeWin.matchMedia('print');
            mql.addListener((m) => {
              if (!m.matches) cleanup();
            });
          }

          iframeWin.print();

          // Safety net: remove iframe if onafterprint never fires
          setTimeout(cleanup, 60_000);
        };

        const proceedWithPrint = () => {
          const hasHeatmap = !!iframeDoc.querySelector('.heatmap-print-wrapper');

          if (isSafari && !hasHeatmap) {
            // Safari prints the document at the iframe's viewport width and shrinks
            // it onto the page (tiny text), clips overflowing charts, and ignores
            // break-inside: avoid. Emulate Chrome's print pipeline instead:
            // 1) apply print styles on screen so measurements match the print layout,
            // 2) lay out at the printable width (Letter portrait: 7.5in = 720px),
            // 3) re-fit the problem chart at that width,
            // 4) manually paginate unbreakable cards.
            freezeRechartsForSafari();
            flattenPrintStylesForSafari();
            iframe.style.width = '720px';
            setTimeout(() => {
              fitProblemChartForSafari();
              paginateForSafariPrint();
              finishPrint();
            }, 400); // let layout settle at the new width
            return;
          }

          if (isSafari && hasHeatmap) {
            // scaleHeatmapToSinglePage (Safari path) injects shrink styles,
            // flattens them on screen, then transform-scales the heatmap to a
            // single page. Give layout a beat to settle first so the natural
            // dimensions it measures are accurate.
            setTimeout(() => {
              scaleHeatmapToSinglePage();
              finishPrint();
            }, 400);
            return;
          }

          scaleHeatmapToSinglePage();
          scaleProblemChartToFitPage();
          finishPrint();
        };

        // Poll for Recharts SVGs (up to 3 s)
        let attempts = 0;
        const maxAttempts = 30;
        const checkInterval = setInterval(() => {
          attempts++;
          if (checkChartsRendered() || attempts >= maxAttempts) {
            clearInterval(checkInterval);
            console.log(`Proceeding with print after ${attempts * 100}ms`);
            proceedWithPrint();
          }
        }, 100);
      }, 500);
    });

    console.log('Print dialog opened successfully');
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error(
      `Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Format filename by removing special characters and spaces
 */
export function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[^a-zA-Z0-9]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Preview PDF in a new tab instead of downloading
 */
export async function previewAccessiblePDF(analysisData: any): Promise<void> {
  // Reuse the same iframe-based export — the print dialog lets the user preview
  await exportToAccessiblePDF(analysisData);
}
